import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import TopAppBar from '../components/TopAppBar';
import BottomNavBar from '../components/BottomNavBar';
import { getContractorAdminDetail, reviewCredential, verifyContractor, setFeaturedContractor, setContractorTier } from '../api/admin';
import { downloadCredentialDocument } from '../api/credential';
import { ContractorAdminDetailDto } from '../types/admin';

const CREDENTIAL_META: Record<string, { label: string; icon: string }> = {
  CompanyRegistration: { label: 'Company Registration', icon: 'badge' },
  Insurance:           { label: 'Insurance',            icon: 'policy' },
  BBBEE:               { label: 'B-BBEE',               icon: 'diversity_3' },
  NHBRC:               { label: 'NHBRC',                icon: 'home_work' },
};

const AdminContractorReviewPage: React.FC = () => {
  const { contractorId } = useParams<{ contractorId: string }>();
  const navigate = useNavigate();
  const id = Number(contractorId);

  const [contractor, setContractor] = useState<ContractorAdminDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null); // tracks which field is saving
  const [rejectingId, setRejectingId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    getContractorAdminDetail(id)
      .then(setContractor)
      .catch(() => setError('Failed to load contractor.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleConfirmCredential = async (credId: number) => {
    if (!contractor) return;
    setSaving(`cred-${credId}`);
    try {
      await reviewCredential(credId, 'Confirmed');
      setContractor(prev => prev ? {
        ...prev,
        credentials: prev.credentials.map(c =>
          c.id === credId ? { ...c, status: 'Confirmed', rejectionReason: undefined } : c
        ),
      } : prev);
    } catch {
      setError('Failed to confirm credential.');
    } finally {
      setSaving(null);
    }
  };

  const handleRejectCredential = async (credId: number) => {
    if (!contractor || !rejectReason.trim()) return;
    setSaving(`cred-${credId}`);
    try {
      await reviewCredential(credId, 'Rejected', rejectReason.trim());
      setContractor(prev => prev ? {
        ...prev,
        credentials: prev.credentials.map(c =>
          c.id === credId ? { ...c, status: 'Rejected', rejectionReason: rejectReason.trim() } : c
        ),
      } : prev);
      setRejectingId(null);
      setRejectReason('');
    } catch {
      setError('Failed to reject credential.');
    } finally {
      setSaving(null);
    }
  };

  const handleVerify = async (isVerified: boolean) => {
    if (!contractor) return;
    setSaving('verify');
    try {
      await verifyContractor(id, isVerified, contractor.status ?? 'Active');
      setContractor(prev => prev ? { ...prev, isVerified } : prev);
    } catch {
      setError('Failed to update verification.');
    } finally {
      setSaving(null);
    }
  };

  const handleStatusChange = async (status: string) => {
    if (!contractor) return;
    setSaving('status');
    try {
      await verifyContractor(id, contractor.isVerified, status);
      setContractor(prev => prev ? { ...prev, status } : prev);
    } catch {
      setError('Failed to update status.');
    } finally {
      setSaving(null);
    }
  };

  const handleFeatured = async (isFeatured: boolean) => {
    if (!contractor) return;
    setSaving('featured');
    try {
      await setFeaturedContractor(id, isFeatured);
      setContractor(prev => prev ? { ...prev, isFeatured } : prev);
    } catch {
      setError('Failed to update featured status.');
    } finally {
      setSaving(null);
    }
  };

  const handleTierChange = async (tier: number | null) => {
    if (!contractor) return;
    setSaving('tier');
    try {
      await setContractorTier(id, tier);
      setContractor(prev => prev ? { ...prev, tier: tier ?? undefined } : prev);
    } catch {
      setError('Failed to update tier.');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="text-outline">Loading...</span>
      </div>
    );
  }

  if (error || !contractor) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="text-error">{error ?? 'Contractor not found.'}</span>
      </div>
    );
  }

  const allConfirmed = contractor.credentials.length > 0 && contractor.credentials.every(c => c.status === 'Confirmed');

  return (
    <div className="min-h-screen bg-surface-container-lowest pb-24 md:pb-8">
      <TopAppBar />

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">

        {/* Back */}
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center gap-1 text-sm text-outline hover:text-on-surface transition-colors"
        >
          <span className="material-symbols-outlined text-[18px]">arrow_back</span>
          Back to dashboard
        </button>

        {/* Header card */}
        <div className="bg-surface rounded-2xl shadow-sm p-6 flex flex-col md:flex-row gap-6">
          {contractor.profilePictureUrl ? (
            <img
              src={contractor.profilePictureUrl}
              alt={contractor.companyName}
              className="w-20 h-20 rounded-full object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <span className="material-symbols-outlined text-primary text-3xl">business</span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-start gap-3 mb-1">
              <h1 className="text-2xl font-bold text-on-surface">
                {contractor.companyName ?? 'Unnamed Company'}
              </h1>
              {contractor.isVerified && (
                <span className="flex items-center gap-1 bg-primary/10 text-primary text-xs font-semibold px-2 py-1 rounded-full">
                  <span className="material-symbols-outlined text-[14px]">verified</span>
                  Verified
                </span>
              )}
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                contractor.status === 'Active' ? 'bg-green-100 text-green-700' :
                contractor.status === 'Suspended' ? 'bg-red-100 text-red-700' :
                'bg-yellow-100 text-yellow-700'
              }`}>
                {contractor.status ?? 'Pending'}
              </span>
            </div>
            <p className="text-sm text-outline mb-3">
              {[contractor.city, contractor.province].filter(Boolean).join(', ')}
              {contractor.contactEmail && ` · ${contractor.contactEmail}`}
            </p>
            {contractor.bio && (
              <p className="text-sm text-on-surface-variant line-clamp-3">{contractor.bio}</p>
            )}
          </div>
        </div>

        {/* Admin controls */}
        <div className="bg-surface rounded-2xl shadow-sm p-6">
          <h2 className="font-semibold text-on-surface mb-4">Admin Controls</h2>
          <div className="flex flex-wrap gap-4 items-center">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <div
                onClick={() => !saving && handleVerify(!contractor.isVerified)}
                className={`relative w-12 h-6 rounded-full transition-colors ${contractor.isVerified ? 'bg-primary' : 'bg-outline-variant'} ${saving === 'verify' ? 'opacity-50 cursor-wait' : ''}`}
              >
                <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${contractor.isVerified ? 'translate-x-6' : ''}`} />
              </div>
              <span className="text-sm font-medium text-on-surface">Verified</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <div
                onClick={() => !saving && handleFeatured(!contractor.isFeatured)}
                className={`relative w-12 h-6 rounded-full transition-colors ${contractor.isFeatured ? 'bg-secondary' : 'bg-outline-variant'} ${saving === 'featured' ? 'opacity-50 cursor-wait' : ''}`}
              >
                <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${contractor.isFeatured ? 'translate-x-6' : ''}`} />
              </div>
              <span className="text-sm font-medium text-on-surface">Featured</span>
            </label>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-on-surface">Status</span>
              <select
                value={contractor.status ?? 'Under Review'}
                onChange={e => handleStatusChange(e.target.value)}
                disabled={saving === 'status'}
                className="text-sm border border-outline-variant rounded-lg px-3 py-1.5 bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              >
                <option value="Approved">Approved</option>
                <option value="Probationary">Probationary</option>
                <option value="Blacklisted">Blacklisted</option>
                <option value="Under Review">Under Review</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-on-surface">Tier</span>
              <select
                value={contractor.tier ?? ''}
                onChange={e => handleTierChange(e.target.value ? Number(e.target.value) : null)}
                disabled={saving === 'tier'}
                className="text-sm border border-outline-variant rounded-lg px-3 py-1.5 bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              >
                <option value="">No tier</option>
                <option value="1">Economy</option>
                <option value="2">Standard</option>
                <option value="3">Premium</option>
              </select>
            </div>
          </div>
        </div>

        {/* Credentials */}
        <div className="bg-surface rounded-2xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-on-surface">Compliance &amp; Credentials</h2>
            {allConfirmed && (
              <span className="flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded-full">
                <span className="material-symbols-outlined text-[14px]">check_circle</span>
                All confirmed
              </span>
            )}
          </div>

          {contractor.credentials.length === 0 ? (
            <p className="text-sm text-outline italic">No credentials submitted.</p>
          ) : (
            <div className="space-y-4">
              {contractor.credentials.map(cred => {
                const meta = CREDENTIAL_META[cred.type] ?? { label: cred.type, icon: 'description' };
                const isSaving = saving === `cred-${cred.id}`;
                const isRejecting = rejectingId === cred.id;

                const statusConfig = {
                  NotSubmitted: { label: 'Not submitted', classes: 'bg-surface-container text-outline' },
                  UnderReview:  { label: 'Under review',  classes: 'bg-amber-100 text-amber-700' },
                  Confirmed:    { label: 'Confirmed',     classes: 'bg-green-100 text-green-700' },
                  Rejected:     { label: 'Rejected',      classes: 'bg-red-100 text-red-700' },
                }[cred.status] ?? { label: cred.status, classes: 'bg-surface-container text-outline' };

                return (
                  <div
                    key={cred.id}
                    className={`p-4 rounded-xl border transition-colors ${
                      cred.status === 'Confirmed'
                        ? 'border-green-200 bg-green-50'
                        : cred.status === 'Rejected'
                        ? 'border-red-200 bg-red-50'
                        : cred.status === 'UnderReview'
                        ? 'border-amber-200 bg-amber-50'
                        : 'border-outline-variant bg-surface-container-low'
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <span className={`material-symbols-outlined text-2xl mt-0.5 ${
                        cred.status === 'Confirmed' ? 'text-green-600' :
                        cred.status === 'Rejected' ? 'text-red-500' :
                        cred.status === 'UnderReview' ? 'text-amber-600' :
                        'text-outline'
                      }`}>
                        {meta.icon}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <p className="text-sm font-semibold text-on-surface">{meta.label}</p>
                          <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full ${statusConfig.classes}`}>
                            {statusConfig.label}
                          </span>
                        </div>
                        {cred.registrationNumber && (
                          <p className="text-xs text-outline">Reg: {cred.registrationNumber}</p>
                        )}
                        {cred.hasDocument ? (
                          <button
                            onClick={() => downloadCredentialDocument(cred.id)}
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-1"
                          >
                            <span className="material-symbols-outlined text-[14px]">open_in_new</span>
                            View document
                          </button>
                        ) : (
                          <p className="text-xs text-outline-variant mt-0.5 italic">No document uploaded</p>
                        )}
                        {cred.status === 'Rejected' && cred.rejectionReason && (
                          <p className="text-xs text-red-700 mt-1">Reason: {cred.rejectionReason}</p>
                        )}
                      </div>

                      {cred.status === 'UnderReview' && !isRejecting && (
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => !isSaving && handleConfirmCredential(cred.id)}
                            disabled={isSaving}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-600 text-white hover:bg-green-700 transition-all active:scale-95 disabled:opacity-50"
                          >
                            <span className="material-symbols-outlined text-[14px]">check_circle</span>
                            {isSaving ? 'Saving...' : 'Confirm'}
                          </button>
                          <button
                            onClick={() => { setRejectingId(cred.id); setRejectReason(''); }}
                            disabled={isSaving}
                            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-100 text-red-700 hover:bg-red-200 transition-all active:scale-95 disabled:opacity-50"
                          >
                            <span className="material-symbols-outlined text-[14px]">cancel</span>
                            Reject
                          </button>
                        </div>
                      )}
                    </div>

                    {cred.status === 'UnderReview' && isRejecting && (
                      <div className="mt-3 space-y-2">
                        <textarea
                          value={rejectReason}
                          onChange={e => setRejectReason(e.target.value)}
                          placeholder="Reason for rejection (required)"
                          rows={2}
                          className="w-full px-3 py-2 text-sm border border-red-300 rounded-lg bg-white text-on-surface focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleRejectCredential(cred.id)}
                            disabled={isSaving || !rejectReason.trim()}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-600 text-white hover:bg-red-700 transition-all active:scale-95 disabled:opacity-50"
                          >
                            {isSaving ? 'Saving...' : 'Submit rejection'}
                          </button>
                          <button
                            onClick={() => { setRejectingId(null); setRejectReason(''); }}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-outline hover:text-on-surface transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Profile details */}
        <div className="bg-surface rounded-2xl shadow-sm p-6">
          <h2 className="font-semibold text-on-surface mb-4">Profile Details</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
            {[
              { label: 'Phone', value: contractor.phoneNumber },
              { label: 'Website', value: contractor.website },
              { label: 'Address', value: [contractor.address, contractor.city, contractor.province, contractor.postalCode].filter(Boolean).join(', ') },
              { label: 'Experience', value: contractor.yearsOfExperience ? `${contractor.yearsOfExperience} years` : undefined },
              { label: 'Tier', value: contractor.tier ? ['', 'Economy', 'Standard', 'Premium'][contractor.tier] : undefined },
              { label: 'Rating', value: contractor.reviewCount ? `${contractor.averageRating.toFixed(1)} (${contractor.reviewCount} reviews)` : 'No reviews yet' },
            ].map(({ label, value }) => value ? (
              <div key={label}>
                <dt className="text-outline text-xs uppercase tracking-wide">{label}</dt>
                <dd className="text-on-surface mt-0.5">{value}</dd>
              </div>
            ) : null)}
          </dl>

          {contractor.categories.length > 0 && (
            <div className="mt-4">
              <p className="text-xs uppercase tracking-wide text-outline mb-2">Categories</p>
              <div className="flex flex-wrap gap-2">
                {contractor.categories.map(c => (
                  <span key={c} className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">{c}</span>
                ))}
              </div>
            </div>
          )}

          {contractor.serviceAreas.length > 0 && (
            <div className="mt-4">
              <p className="text-xs uppercase tracking-wide text-outline mb-2">Service Areas</p>
              <div className="flex flex-wrap gap-2">
                {contractor.serviceAreas.map(a => (
                  <span key={a} className="bg-surface-container text-on-surface-variant text-xs px-2 py-1 rounded-full">{a}</span>
                ))}
              </div>
            </div>
          )}
        </div>

      </main>

      <BottomNavBar />
    </div>
  );
};

export default AdminContractorReviewPage;
